/**
 * Revoke Invite Modal Component
 *
 * Confirmation modal for revoking an invite.
 *
 * @fileoverview Revoke invite modal component
 */

import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Box,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdWarning } from 'react-icons/md';
import type { AdminInvite } from '../../types/admin';

interface RevokeInviteModalProps {
  invite: AdminInvite | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}

/**
 * Revoke Invite Modal Component
 *
 * Displays a confirmation dialog for revoking an invite.
 */
export const RevokeInviteModal: React.FC<RevokeInviteModalProps> = ({
  invite,
  isOpen,
  onClose,
  onConfirm,
  loading,
}) => {
  const warningBg = useColorModeValue('orange.50', 'orange.900');
  const warningColor = useColorModeValue('orange.600', 'orange.200');

  if (!invite) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Revoke Invite</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box
            p={4}
            borderRadius="md"
            bg={warningBg}
            mb={4}
            display="flex"
            alignItems="flex-start"
            gap={3}
          >
            <Icon as={MdWarning} boxSize={5} color={warningColor} mt={0.5} />
            <Text fontSize="sm" color={warningColor}>
              This action will invalidate the invite link. The invitee will no longer be able
              to use this invite to register.
            </Text>
          </Box>
          <Text>
            Are you sure you want to revoke the invite for{' '}
            <Text as="span" fontWeight="bold">{invite.email}</Text>?
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={loading}>
            Cancel
          </Button>
          <Button
            colorScheme="orange"
            onClick={onConfirm}
            isLoading={loading}
            loadingText="Revoking..."
          >
            Revoke Invite
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RevokeInviteModal;
